import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { X, Plus, Trash, Minus, House, UsersThree, MagnifyingGlass, Check, DotsSixVertical, CaretDown, ArrowUp, ArrowDown } from '@phosphor-icons/react';
import ScrollFadeRow from '../components/ScrollFadeRow';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const EQUIPMENT_TYPES = ['Barbell', 'Dumbbell', 'Free Weight', 'Machine', 'Cable', 'Bodyweight', 'Cardio', 'Kettlebell', 'Band'];
const MUSCLE_GROUPS = ['Chest', 'Shoulders', 'Back', 'Legs', 'Hamstrings', 'Glutes', 'Calves', 'Biceps', 'Triceps', 'Core', 'Forearms', 'Cardio'];

export default function RoutineEditor() {
  const navigate = useNavigate();
  const { routineId } = useParams();
  const isEditing = !!routineId;

  const [groups, setGroups] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [selectedGroupId, setSelectedGroupId] = useState('personal'); // 'personal' or a group id
  const [selectedProgramId, setSelectedProgramId] = useState('');
  const [routineName, setRoutineName] = useState('');
  const [exercises, setExercises] = useState([]);
  const [selectedExercises, setSelectedExercises] = useState([]);
  const [muscleFilter, setMuscleFilter] = useState('');
  const [equipmentFilter, setEquipmentFilter] = useState('');
  const [searchQ, setSearchQ] = useState('');
  const [showCustomForm, setShowCustomForm] = useState(false);
  const [customPrefill, setCustomPrefill] = useState(null);
  const [wiggle, setWiggle] = useState(false);
  const [dragIdx, setDragIdx] = useState(null);
  const [dragOverIdx, setDragOverIdx] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadData(); /* eslint-disable-next-line */ }, [routineId]);

  const loadData = async () => {
    try {
      const [groupsRes, exercisesRes, programsRes] = await Promise.all([
        axios.get(`${API_URL}/groups`, { withCredentials: true }),
        axios.get(`${API_URL}/exercises`, { withCredentials: true }),
        axios.get(`${API_URL}/programs`, { withCredentials: true }),
      ]);
      setGroups(groupsRes.data);
      setExercises(exercisesRes.data);
      setPrograms(programsRes.data);

      if (routineId) {
        const rRes = await axios.get(`${API_URL}/routines/${routineId}`, { withCredentials: true });
        const r = rRes.data;
        setRoutineName(r.name);
        setSelectedGroupId(r.group_id || 'personal');
        setSelectedProgramId(r.program_id || '');
        setSelectedExercises(
          (r.exercises || []).map((e) => ({
            exercise_id: e.exercise_id,
            exercise: e.exercise,
            target_sets: e.target_sets,
          }))
        );
      }
      setLoading(false);
    } catch (e) {
      console.error(e);
      setLoading(false);
    }
  };

  const addExercise = (ex) => {
    // Preserve current scroll position so the library list doesn't jump when
    // the "In This Routine" panel grows above it.
    const y = typeof window !== 'undefined' ? window.scrollY : 0;
    setSelectedExercises((prev) => {
      if (prev.find((e) => e.exercise_id === ex.id)) return prev;
      return [...prev, { exercise_id: ex.id, exercise: ex, target_sets: 3 }];
    });
    if (typeof window !== 'undefined') {
      requestAnimationFrame(() => window.scrollTo({ top: y, behavior: 'auto' }));
    }
  };

  const removeExercise = (id) =>
    setSelectedExercises(selectedExercises.filter((e) => e.exercise_id !== id));

  const updateSets = (id, sets) =>
    setSelectedExercises(
      selectedExercises.map((e) => (e.exercise_id === id ? { ...e, target_sets: sets } : e))
    );

  const reorderTo = (fromIdx, toIdx) => {
    if (fromIdx == null || toIdx == null || fromIdx === toIdx) return;
    const copy = [...selectedExercises];
    const [moved] = copy.splice(fromIdx, 1);
    copy.splice(toIdx, 0, moved);
    setSelectedExercises(copy);
  };

  const moveBy = (idx, delta) => {
    const to = idx + delta;
    if (to < 0 || to >= selectedExercises.length) return;
    reorderTo(idx, to);
  };

  const [showReorderHint, setShowReorderHint] = useState(false);

  const listRef = useRef(null);
  // Tap outside the wiggle list to exit
  useEffect(() => {
    if (!wiggle) return;
    const onDown = (e) => {
      if (listRef.current && !listRef.current.contains(e.target)) {
        setWiggle(false);
      }
    };
    document.addEventListener('mousedown', onDown, true);
    document.addEventListener('touchstart', onDown, true);
    return () => {
      document.removeEventListener('mousedown', onDown, true);
      document.removeEventListener('touchstart', onDown, true);
    };
  }, [wiggle]);

  const nameInputRef = useRef(null);
  const nameFieldRef = useRef(null);
  const [nameError, setNameError] = useState('');
  const [nameShake, setNameShake] = useState(false);

  const saveRoutine = async () => {
    const trimmed = routineName.trim();
    // Validate — routine name is required. Shake + haptic + highlight + hint.
    if (!trimmed) {
      setNameError('Give your routine a name so you can find it later');
      setNameShake(true);
      try { navigator.vibrate && navigator.vibrate([12, 20, 12, 20, 12]); } catch {}
      nameInputRef.current?.focus();
      nameFieldRef.current?.scrollIntoView({ block: 'center', behavior: 'smooth' });
      setTimeout(() => setNameShake(false), 500);
      return;
    }
    if (selectedExercises.length === 0) {
      alert('Add at least one exercise to this routine.');
      return;
    }
    setNameError('');
    try {
      const gid = selectedGroupId === 'personal' ? null : selectedGroupId;
      const pid = selectedProgramId || null;
      let targetId = routineId;

      if (!isEditing) {
        const { data } = await axios.post(
          `${API_URL}/routines`,
          { name: routineName, group_id: gid, program_id: pid },
          { withCredentials: true }
        );
        targetId = data.id;
      }
      await axios.put(
        `${API_URL}/routines/${targetId}`,
        {
          name: routineName,
          group_id: gid,
          program_id: pid,
          exercises: selectedExercises.map((e) => ({
            exercise_id: e.exercise_id,
            target_sets: e.target_sets,
          })),
        },
        { withCredentials: true }
      );
      navigate('/');
    } catch (e) {
      console.error(e);
      alert('Could not save routine');
    }
  };

  const createCustomExercise = async (payload) => {
    try {
      const { data } = await axios.post(`${API_URL}/exercises`, payload, {
        withCredentials: true,
      });
      setExercises((prev) => [...prev, data]);
      addExercise(data);
      setShowCustomForm(false);
      setCustomPrefill(null);
      setSearchQ('');
    } catch (e) {
      console.error(e);
      alert('Could not create custom exercise');
    }
  };

  const openCustomWithName = (name) => {
    setCustomPrefill({ name });
    setShowCustomForm(true);
  };

  const deleteRoutine = async () => {
    if (!routineId) return;
    if (!window.confirm('Delete this routine? This cannot be undone.')) return;
    try {
      await axios.delete(`${API_URL}/routines/${routineId}`, { withCredentials: true });
      navigate('/');
    } catch (e) {
      alert('Could not delete routine');
    }
  };

  if (loading) {
    return (
      <div className="mobile-container min-h-screen flex items-center justify-center">
        <div className="animate-pulse-soft text-6xl font-black font-heading text-graphite">B</div>
      </div>
    );
  }

  const q = searchQ.trim().toLowerCase();
  const filtered = exercises.filter(
    (e) =>
      (muscleFilter === '' || e.muscle_group === muscleFilter) &&
      (equipmentFilter === '' || e.equipment_type === equipmentFilter) &&
      (q === '' || e.name.toLowerCase().includes(q))
  );
  const byMuscle = filtered.reduce((acc, e) => {
    (acc[e.muscle_group] = acc[e.muscle_group] || []).push(e);
    return acc;
  }, {});

  return (
    <div className="mobile-container min-h-screen">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-bone/90 backdrop-blur-md border-b border-hairline/60 px-5 py-4">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-black font-heading uppercase text-graphite">
            {isEditing ? 'Edit Routine' : 'New Routine'}
          </h1>
          <button
            onClick={() => navigate(-1)}
            className="w-10 h-10 rounded-ios-pill border border-hairline flex items-center justify-center"
            data-testid="close-button"
          >
            <X size={20} weight="bold" />
          </button>
        </div>
      </div>

      <div className="px-5 pt-5 pb-40">
        {/* Routine name */}
        <div className={`mb-4 ${nameShake ? 'shake-x' : ''}`} ref={nameFieldRef}>
          <label className="block text-[10px] uppercase tracking-[0.22em] font-bold text-graphite/50 mb-1.5">
            Routine Name
          </label>
          <input
            ref={nameInputRef}
            type="text"
            value={routineName}
            onChange={(e) => {
              setRoutineName(e.target.value);
              if (nameError && e.target.value.trim()) setNameError('');
            }}
            className={`w-full h-14 px-4 rounded-ios-pill border bg-boneWarm focus:outline-none text-base ${
              nameError
                ? 'field-invalid'
                : 'border-hairline focus:ring-2 focus:ring-graphite'
            }`}
            placeholder="Upper Body Day"
            aria-invalid={!!nameError}
            aria-describedby="routine-name-hint"
            data-testid="routine-name-input"
          />
          {nameError && (
            <p
              id="routine-name-hint"
              className="mt-2 text-[11px] uppercase tracking-[0.14em] font-bold text-userRed"
              data-testid="routine-name-error"
            >
              {nameError}
            </p>
          )}
        </div>

        {/* Group scope */}
        <div className="mb-4">
          <label className="block text-[10px] uppercase tracking-[0.22em] font-bold text-graphite/50 mb-1.5">
            Share With
          </label>
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={() => setSelectedGroupId('personal')}
              className={`h-11 px-4 rounded-ios-pill text-xs font-black uppercase tracking-[0.15em] flex items-center gap-1.5 ${
                selectedGroupId === 'personal'
                  ? 'bg-graphite text-bone'
                  : 'bg-graphite/5 text-graphite/60'
              }`}
              data-testid="scope-personal-button"
            >
              <House size={14} weight="fill" />
              Personal
            </button>
            {groups.map((g) => (
              <button
                key={g.id}
                onClick={() => setSelectedGroupId(g.id)}
                className={`h-11 px-4 rounded-ios-pill text-xs font-black uppercase tracking-[0.15em] flex items-center gap-1.5 ${
                  selectedGroupId === g.id
                    ? 'bg-partnerBlue text-bone'
                    : 'bg-graphite/5 text-graphite/60'
                }`}
                data-testid={`scope-group-${g.id}`}
              >
                <UsersThree size={14} weight="fill" />
                {g.name}
              </button>
            ))}
          </div>
        </div>

        {/* Program (optional) */}
        {programs.length > 0 && (
          <div className="mb-6">
            <label className="block text-[10px] uppercase tracking-[0.22em] font-bold text-graphite/50 mb-1.5">
              Program
            </label>
            <select
              value={selectedProgramId}
              onChange={(e) => setSelectedProgramId(e.target.value)}
              className="w-full h-12 px-4 rounded-ios-pill border border-hairline bg-boneWarm text-sm font-semibold"
              data-testid="program-select"
            >
              <option value="">— None —</option>
              {programs.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </div>
        )}

        {/* Selected exercises */}
        {selectedExercises.length > 0 && (
          <div className="mb-6">
            <div className="flex items-baseline justify-between mb-2">
              <h3 className="text-lg font-black font-heading uppercase text-graphite">
                In This Routine
              </h3>
              <button
                onClick={() => setShowReorderHint((v) => !v)}
                className="text-[10px] uppercase tracking-[0.18em] font-bold text-graphite/40 hover:text-graphite flex items-center gap-1"
                data-testid="reorder-hint-toggle"
              >
                How to reorder
                <CaretDown
                  size={10}
                  weight="bold"
                  className={`transition-transform ${showReorderHint ? 'rotate-180' : ''}`}
                />
              </button>
            </div>
            {showReorderHint && (
              <div
                className="mb-3 text-[11px] leading-relaxed text-graphite/70 bg-boneWarm border border-hairline rounded-ios px-3 py-2 animate-slide-up"
                data-testid="reorder-hint-body"
              >
                Tap the <span className="font-black">⋮⋮ handle</span> and drag a row, or use the <span className="font-black">↑ ↓</span> arrows to nudge one row at a time. Row-drag never selects the text, so long-press won't highlight anything.
              </div>
            )}
            <div className="space-y-2" ref={listRef} data-testid="selected-exercise-list">
              {selectedExercises.map((item, idx) => (
                <WiggleRow
                  key={item.exercise_id}
                  item={item}
                  index={idx}
                  total={selectedExercises.length}
                  wiggle={wiggle}
                  isDragging={dragIdx === idx}
                  isDragTarget={dragOverIdx === idx && dragIdx !== null && dragIdx !== idx}
                  onLongPressStart={() => setWiggle(true)}
                  onDragStart={() => setDragIdx(idx)}
                  onDragEnter={() => setDragOverIdx(idx)}
                  onDragEnd={() => {
                    reorderTo(dragIdx, dragOverIdx);
                    setDragIdx(null);
                    setDragOverIdx(null);
                  }}
                  onMoveUp={() => moveBy(idx, -1)}
                  onMoveDown={() => moveBy(idx, 1)}
                  onDecSets={() => updateSets(item.exercise_id, Math.max(1, item.target_sets - 1))}
                  onIncSets={() => updateSets(item.exercise_id, item.target_sets + 1)}
                  onRemove={() => removeExercise(item.exercise_id)}
                />
              ))}
            </div>
          </div>
        )}

        {/* Exercise library */}
        <div className="mb-4">
          <div className="flex items-baseline justify-between mb-3">
            <h3 className="text-lg font-black font-heading uppercase text-graphite">
              Add Exercises
            </h3>
            <button
              onClick={() => setShowCustomForm(true)}
              className="text-xs uppercase tracking-[0.15em] font-bold text-userRed flex items-center gap-1"
              data-testid="show-custom-form-button"
            >
              <Plus size={14} weight="bold" /> Custom
            </button>
          </div>

          {/* Search */}
          <div className="relative mb-3">
            <MagnifyingGlass
              size={16}
              weight="bold"
              className="absolute left-4 top-1/2 -translate-y-1/2 text-graphite/40"
            />
            <input
              type="search"
              inputMode="search"
              enterKeyHint="search"
              autoCapitalize="none"
              autoCorrect="off"
              value={searchQ}
              onChange={(e) => setSearchQ(e.target.value)}
              placeholder="Search exercises"
              className="w-full h-12 pl-11 pr-10 rounded-ios-pill border border-hairline bg-boneWarm focus:outline-none focus:ring-2 focus:ring-graphite text-base"
              data-testid="exercise-search-input"
            />
            {searchQ && (
              <button
                onClick={() => setSearchQ('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-graphite/10 flex items-center justify-center"
                data-testid="clear-search-button"
                aria-label="Clear search"
              >
                <X size={12} weight="bold" />
              </button>
            )}
          </div>

          {/* Filter chips */}
          <ScrollFadeRow className="flex gap-1.5 pb-2 mb-2">
            <FilterChip active={muscleFilter === ''} onClick={() => setMuscleFilter('')}>All</FilterChip>
            {MUSCLE_GROUPS.map((mg) => (
              <FilterChip key={mg} active={muscleFilter === mg} onClick={() => setMuscleFilter(mg)}>{mg}</FilterChip>
            ))}
          </ScrollFadeRow>
          <ScrollFadeRow className="flex gap-1.5 pb-2 mb-3">
            <FilterChip active={equipmentFilter === ''} onClick={() => setEquipmentFilter('')}>All Types</FilterChip>
            {EQUIPMENT_TYPES.map((et) => (
              <FilterChip key={et} active={equipmentFilter === et} onClick={() => setEquipmentFilter(et)}>{et}</FilterChip>
            ))}
          </ScrollFadeRow>

          {/* No results — offer to create */}
          {filtered.length === 0 && (
            <button
              onClick={() => openCustomWithName(searchQ || '')}
              className="w-full ios-card p-4 flex items-center justify-between border-2 border-dashed border-userRed/40 hover:border-userRed hover:bg-userRed/5"
              data-testid="create-from-search-button"
            >
              <div className="text-left">
                <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-userRed leading-none">
                  Not in the library?
                </p>
                <p className="text-base font-black font-heading uppercase text-graphite mt-1">
                  Create {searchQ ? `"${searchQ}"` : 'a custom exercise'}
                </p>
              </div>
              <span className="w-9 h-9 rounded-full bg-userRed text-bone flex items-center justify-center">
                <Plus size={16} weight="bold" />
              </span>
            </button>
          )}

          <div className="space-y-4">
            {Object.entries(byMuscle).map(([mg, exs]) => (
              <div key={mg}>
                <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-graphite/50 mb-1.5">
                  {mg}
                </p>
                <div className="space-y-1">
                  {exs.map((ex) => {
                    const picked = !!selectedExercises.find((e) => e.exercise_id === ex.id);
                    return (
                      <button
                        key={ex.id}
                        onClick={() => !picked && addExercise(ex)}
                        disabled={picked}
                        className={`w-full text-left px-4 py-3 rounded-ios border transition-colors ${
                          picked
                            ? 'bg-userRed/10 border-userRed/40 text-graphite'
                            : 'bg-boneWarm border-hairline hover:border-graphite'
                        }`}
                        data-testid={`library-exercise-${ex.id}`}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex items-center gap-2 min-w-0">
                            {picked && (
                              <span
                                className="flex-shrink-0 w-5 h-5 rounded-full bg-userRed text-bone flex items-center justify-center"
                                data-testid={`library-exercise-added-${ex.id}`}
                              >
                                <Check size={12} weight="bold" />
                              </span>
                            )}
                            <span className={`text-sm font-bold truncate ${picked ? 'text-userRed' : ''}`}>
                              {ex.name}
                            </span>
                          </div>
                          <span className={`text-[10px] uppercase tracking-[0.15em] font-bold flex-shrink-0 ${picked ? 'text-userRed/70' : 'text-graphite/50'}`}>
                            {(ex.equipment_type || '').replace(/^Cardio\s*[-·]\s*/i, '').trim() || ex.equipment_type}
                            {ex.is_custom && ' · Custom'}
                          </span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>

        <button
          onClick={saveRoutine}
          className="w-full h-16 bg-userRed text-bone font-black text-base uppercase tracking-[0.15em] rounded-ios-pill sticky z-30 shadow-ios"
          style={{ bottom: 'calc(88px + env(safe-area-inset-bottom))' }}
          data-testid="save-routine-button"
        >
          {isEditing ? 'Save Changes' : 'Create Routine'}
        </button>

        {isEditing && (
          <div className="mt-8 pt-6 border-t border-hairline">
            <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-graphite/50 mb-2">
              Danger Zone
            </p>
            <button
              onClick={deleteRoutine}
              className="w-full h-14 bg-userRed/8 text-userRed border border-userRed/25 font-black text-sm uppercase tracking-[0.15em] rounded-ios-pill flex items-center justify-center gap-2 hover:bg-userRed hover:text-bone hover:border-userRed"
              data-testid="delete-routine-button"
            >
              <Trash size={16} weight="bold" />
              Delete This Routine
            </button>
          </div>
        )}
      </div>

      {showCustomForm && (
        <CustomExerciseForm
          prefill={customPrefill}
          onCreate={createCustomExercise}
          onClose={() => { setShowCustomForm(false); setCustomPrefill(null); }}
        />
      )}
    </div>
  );
}

/* WiggleRow — a dedicated ⋮⋮ grab handle owns the drag lifecycle. The rest
   of the row (title, muscle text) is guaranteed to never trigger a drag,
   which means long-press can't select the text underneath. Users who don't
   want to drag can also just tap the ↑ / ↓ arrows on the right. */
function WiggleRow({
  item, index, total, wiggle, isDragging, isDragTarget,
  onLongPressStart, onDragStart, onDragEnter, onDragEnd,
  onMoveUp, onMoveDown,
  onDecSets, onIncSets, onRemove,
}) {
  const holdTimer = useRef(null);
  const pointerActiveRef = useRef(false);

  const clearHold = () => {
    if (holdTimer.current) {
      clearTimeout(holdTimer.current);
      holdTimer.current = null;
    }
  };

  // Drag lifecycle — only fires from the dedicated grab handle.
  const handleHandlePointerDown = (e) => {
    e.preventDefault();  // stop the browser from initiating a text selection
    if (wiggle) {
      onDragStart();
      pointerActiveRef.current = true;
      return;
    }
    clearHold();
    holdTimer.current = setTimeout(() => {
      onLongPressStart();
      onDragStart();
      pointerActiveRef.current = true;
      try { navigator.vibrate && navigator.vibrate(15); } catch {}
    }, 400);
  };

  const handleRowPointerEnter = () => {
    if (!wiggle) return;
    onDragEnter();
  };

  const handleGlobalPointerUp = () => {
    clearHold();
    if (pointerActiveRef.current) {
      onDragEnd();
      pointerActiveRef.current = false;
    }
  };

  const cardClass = [
    'ios-card p-3 flex items-center gap-2 transition-shadow select-none',
    wiggle ? 'wiggle-item' : '',
    isDragging ? 'dragging' : '',
    isDragTarget ? 'drag-target' : '',
  ].filter(Boolean).join(' ');

  return (
    <div
      className={cardClass}
      onPointerEnter={handleRowPointerEnter}
      onPointerUp={handleGlobalPointerUp}
      onPointerCancel={handleGlobalPointerUp}
      onPointerLeave={clearHold}
      data-testid={`selected-exercise-${index}`}
    >
      {/* Grab handle — the ONLY drag hit-target. */}
      <button
        type="button"
        onPointerDown={handleHandlePointerDown}
        className={`w-9 h-9 -ml-1 flex items-center justify-center rounded-ios-pill flex-shrink-0 touch-none ${
          wiggle ? 'bg-userRed/15 text-userRed' : 'text-graphite/40 hover:bg-graphite/5'
        }`}
        style={{ touchAction: 'none', userSelect: 'none', cursor: wiggle ? 'grabbing' : 'grab' }}
        data-testid={`drag-handle-${index}`}
        aria-label="Drag to reorder"
      >
        <DotsSixVertical size={18} weight="bold" />
      </button>

      <div className="flex-1 min-w-0">
        <p className="text-sm font-black uppercase text-graphite truncate">
          {item.exercise.name}
        </p>
        <p className="text-[10px] uppercase tracking-[0.15em] text-graphite/50 font-semibold">
          {item.exercise.muscle_group === 'Cardio'
            ? ((item.exercise.equipment_type || '').replace(/^Cardio\s*[-·]\s*/i, '').trim() || 'Cardio')
            : `${item.exercise.muscle_group} · ${item.exercise.equipment_type}`}
        </p>
      </div>
      {/* Arrows — non-drag reorder path */}
      <div className="flex flex-col gap-0.5 flex-shrink-0">
        <button
          onClick={(e) => { e.stopPropagation(); onMoveUp(); }}
          disabled={index === 0}
          className="w-6 h-5 rounded flex items-center justify-center text-graphite/50 hover:text-userRed disabled:opacity-25"
          data-testid={`move-up-${index}`}
          aria-label="Move up"
        >
          <ArrowUp size={11} weight="bold" />
        </button>
        <button
          onClick={(e) => { e.stopPropagation(); onMoveDown(); }}
          disabled={index === total - 1}
          className="w-6 h-5 rounded flex items-center justify-center text-graphite/50 hover:text-userRed disabled:opacity-25"
          data-testid={`move-down-${index}`}
          aria-label="Move down"
        >
          <ArrowDown size={11} weight="bold" />
        </button>
      </div>
      <div className="flex items-center gap-1.5">
        <button
          onClick={(e) => { e.stopPropagation(); onDecSets(); }}
          className="w-8 h-8 rounded-ios-pill bg-graphite/5 flex items-center justify-center"
          data-testid={`sets-minus-${index}`}
        >
          <Minus size={14} weight="bold" />
        </button>
        <span className="w-6 text-center text-lg font-black font-heading tabular-nums text-graphite">
          {item.target_sets}
        </span>
        <button
          onClick={(e) => { e.stopPropagation(); onIncSets(); }}
          className="w-8 h-8 rounded-ios-pill bg-graphite/5 flex items-center justify-center"
          data-testid={`sets-plus-${index}`}
        >
          <Plus size={14} weight="bold" />
        </button>
      </div>
      <button
        onClick={(e) => { e.stopPropagation(); onRemove(); }}
        className="w-8 h-8 text-graphite/40 hover:text-userRed flex items-center justify-center"
        data-testid={`remove-selected-${index}`}
      >
        <Trash size={14} weight="bold" />
      </button>
    </div>
  );
}

function FilterChip({ active, onClick, children }) {
  return (
    <button
      onClick={onClick}
      className={`flex-shrink-0 h-8 px-3 rounded-ios-pill text-[10px] uppercase tracking-[0.15em] font-bold ${
        active ? 'bg-graphite text-bone' : 'bg-graphite/5 text-graphite/60'
      }`}
    >
      {children}
    </button>
  );
}

function CustomExerciseForm({ prefill, onCreate, onClose }) {
  const [name, setName] = useState(prefill?.name || '');
  const [muscleGroup, setMuscleGroup] = useState(MUSCLE_GROUPS[0]);
  const [equipmentType, setEquipmentType] = useState(EQUIPMENT_TYPES[0]);

  return (
    <div className="fixed inset-0 z-[1100] bg-graphite/40 backdrop-blur-sm flex items-end sm:items-center justify-center px-3 pb-3">
      <div className="w-full max-w-md bg-bone rounded-ios-xl shadow-ios overflow-hidden animate-slide-up">
        <div className="px-5 py-5 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-black font-heading uppercase text-graphite">
              Custom Exercise
            </h2>
            <button onClick={onClose} className="w-10 h-10 rounded-ios-pill border border-hairline flex items-center justify-center" data-testid="close-custom-button">
              <X size={18} weight="bold" />
            </button>
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.22em] font-bold text-graphite/50 mb-1.5">
              Name
            </label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full h-12 px-4 rounded-ios-pill border border-hairline bg-boneWarm focus:outline-none focus:ring-2 focus:ring-graphite text-base"
              placeholder="e.g. Landmine Press"
              data-testid="custom-name-input"
              autoFocus
            />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.22em] font-bold text-graphite/50 mb-1.5">
              Type
            </label>
            <div className="flex gap-1.5 flex-wrap">
              {EQUIPMENT_TYPES.map((t) => (
                <button
                  key={t}
                  onClick={() => setEquipmentType(t)}
                  className={`h-9 px-3 rounded-ios-pill text-[10px] uppercase tracking-[0.15em] font-bold ${
                    equipmentType === t ? 'bg-graphite text-bone' : 'bg-graphite/5 text-graphite/60'
                  }`}
                  data-testid={`custom-type-${t}`}
                >
                  {t}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.22em] font-bold text-graphite/50 mb-1.5">
              Muscle Group
            </label>
            <div className="flex gap-1.5 flex-wrap">
              {MUSCLE_GROUPS.map((m) => (
                <button
                  key={m}
                  onClick={() => setMuscleGroup(m)}
                  className={`h-9 px-3 rounded-ios-pill text-[10px] uppercase tracking-[0.15em] font-bold ${
                    muscleGroup === m ? 'bg-userRed text-bone' : 'bg-graphite/5 text-graphite/60'
                  }`}
                  data-testid={`custom-muscle-${m}`}
                >
                  {m}
                </button>
              ))}
            </div>
          </div>
          <button
            onClick={() => name.trim() && onCreate({ name: name.trim(), muscle_group: muscleGroup, equipment_type: equipmentType })}
            disabled={!name.trim()}
            className="w-full h-14 bg-userRed text-bone font-black text-sm uppercase tracking-[0.15em] rounded-ios-pill disabled:opacity-40"
            data-testid="create-custom-button"
          >
            Create Exercise
          </button>
        </div>
      </div>
    </div>
  );
}
